'use client';

import { useCallback, useMemo } from 'react';
import { useDebounceValue } from 'usehooks-ts';
import type { LifecycleStateFilterDto } from '@terra/shared-dto-types';
import {
  constrainSourceFilterValuesToOrigins,
  shouldShowSignalSourceFilter,
} from '@terra/shared-contracts-signals';
import { useAppTargetId, useTargetType } from '../../../store/projectStore';
import {
  useTabScopedSignalsFilters,
  type SignalOrigin,
} from '../../../store/tab/signalsTabScopedFilterStore';
import { useArchivedLifecycleFilter } from '../../../hooks/useArchivedLifecycleFilter';
import { SIGNAL_SEARCH_DEBOUNCE_MS } from '../../../hooks/useSignals';
import { useSignalSourceFilterOptions } from '../../../hooks/api/useSignalSourceFilterOptions';
import { TARGET_TYPE, isAppTarget } from '../../../utils/target-scope.utils';
import { needsUnfilteredSignalTotal } from '../../../utils/needs-unfiltered-signal-total';
import { resolveCreatedStartBound } from '../../../utils/created-since';
import {
  buildSignalCountsFilters,
  buildSignalListFilters,
  buildSignalTotalCountsFilters,
  hasActiveSignalAdvancedFilters,
  hasActiveSignalListFilters,
  isSignalLifecycleFilterActive,
  resolveSignalCategoryFilter,
  type SignalsListScope,
} from './signals-list-filters.model';
import type { SignalsSort } from './signals-list.model';
import type { SignalsSortState } from './signals-grouping.model';

export function useSignalsListFilters({
  scope,
  enableRejectedSignalsToggle,
  lifecycleStateOverride,
}: {
  scope: SignalsListScope;
  enableRejectedSignalsToggle: boolean;
  lifecycleStateOverride: LifecycleStateFilterDto | undefined;
}) {
  const targetType = useTargetType();
  const appTargetId = useAppTargetId();
  const store = useTabScopedSignalsFilters();
  const { sourceFilter, setOriginFilter, setSourceFilter } = store;
  const isNetworkTarget = targetType != null && !isAppTarget(targetType);
  const showAppWide =
    !isNetworkTarget && targetType !== TARGET_TYPE.AI_APPLICATION;
  const isRejectedSignalsView =
    enableRejectedSignalsToggle && store.viewRejectedSignals;
  const lifecycleState = lifecycleStateOverride ?? store.lifecycleState;
  const lifecycle = useArchivedLifecycleFilter({
    lifecycleState,
    lifecycleStateOverride,
  });
  const { lifecycleQueryParam } = lifecycle;
  const { hasThirdPartySignals } = useSignalSourceFilterOptions({
    applicationId: appTargetId,
    enabled: !isNetworkTarget,
  });
  const showThirdPartySource =
    !isNetworkTarget &&
    shouldShowSignalSourceFilter({
      hasThirdPartySignals,
      selectedSources: sourceFilter,
    });
  const canSort = !isNetworkTarget;
  const canGroup = canSort && !isRejectedSignalsView;
  const showAssetGrouping = targetType === TARGET_TYPE.AI_APPLICATION;
  const groupingField = store.grouping?.field;
  const isGroupingAvailable =
    (groupingField !== 'source' || showThirdPartySource) &&
    (groupingField !== 'asset' || showAssetGrouping);
  const grouping = canGroup && isGroupingAvailable ? store.grouping : null;
  const values = useMemo(
    () => ({
      ...store,
      grouping,
      createdStartDate: resolveCreatedStartBound(store),
    }),
    [grouping, store],
  );
  const [debouncedSearchTerm] = useDebounceValue(
    store.searchTerm,
    SIGNAL_SEARCH_DEBOUNCE_MS,
  );
  const [debouncedUrlSearchTerm] = useDebounceValue(
    store.urlSearchTerm,
    SIGNAL_SEARCH_DEBOUNCE_MS,
  );
  const countsValues = useMemo(
    () => ({
      ...values,
      searchTerm: debouncedSearchTerm,
      urlSearchTerm: debouncedUrlSearchTerm,
    }),
    [debouncedSearchTerm, debouncedUrlSearchTerm, values],
  );
  const resolvedCategoryFilter = useMemo(
    () =>
      resolveSignalCategoryFilter({
        targetType,
        isNetworkTarget,
        categoryFilter: store.categoryFilter,
      }),
    [isNetworkTarget, store.categoryFilter, targetType],
  );
  const filters = useMemo(
    () =>
      buildSignalListFilters({
        values,
        resolvedCategoryFilter,
        lifecycleQueryParam,
        isRejectedView: isRejectedSignalsView,
      }),
    [
      isRejectedSignalsView,
      lifecycleQueryParam,
      resolvedCategoryFilter,
      values,
    ],
  );
  const countsFilters = useMemo(
    () =>
      buildSignalCountsFilters({
        values: countsValues,
        resolvedCategoryFilter,
        lifecycleQueryParam,
        isRejectedView: isRejectedSignalsView,
        scope,
      }),
    [
      countsValues,
      isRejectedSignalsView,
      lifecycleQueryParam,
      resolvedCategoryFilter,
      scope,
    ],
  );
  const totalCountsFilters = useMemo(
    () => buildSignalTotalCountsFilters(scope),
    [scope],
  );
  const handleOriginFilterChange = useCallback(
    (origins: SignalOrigin) => {
      setOriginFilter(origins);
      setSourceFilter(
        constrainSourceFilterValuesToOrigins({
          sources: sourceFilter,
          origins,
        }) ?? [],
      );
    },
    [setOriginFilter, setSourceFilter, sourceFilter],
  );
  const sort = useMemo(
    (): SignalsSort => ({ field: store.sortField, order: store.sortOrder }),
    [store.sortField, store.sortOrder],
  );
  const sortState = useMemo(
    (): SignalsSortState => ({ sort, grouping }),
    [grouping, sort],
  );

  return {
    ...store,
    grouping,
    canSort,
    canGroup,
    lifecycleState,
    lifecycle,
    isNetworkTarget,
    isRejectedSignalsView,
    showThirdPartySource,
    showAssetGrouping,
    showAppWide,
    filters,
    countsFilters,
    totalCountsFilters,
    fetchUnfilteredSignalTotal: needsUnfilteredSignalTotal({
      ...values,
      lifecycleQueryParam,
    }),
    hasActiveFilters:
      hasActiveSignalListFilters(values) ||
      isSignalLifecycleFilterActive({
        showLifecycleSelector: lifecycle.showLifecycleSelector,
        lifecycleState,
      }),
    hasAdvancedFilters: hasActiveSignalAdvancedFilters({
      values: store,
      isNetworkTarget,
    }),
    sort,
    sortState,
    setOriginFilter: handleOriginFilterChange,
  };
}

export type SignalsListFilters = ReturnType<typeof useSignalsListFilters>;
